import {
  Card,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useGetWhatsAppConfigQuery, usePatchWhatsAppConfigMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

const whatsAppValidationSchema = z
  .object({
    enabled: z.boolean(),
    appId: z.string().max(255).optional().nullable(),
    appSecret: z.string().max(65535).optional().nullable(),
    wabaId: z.string().max(255).optional().nullable(),
    phoneNumber: z.string().max(255).optional().nullable(),
    permanentToken: z.string().max(65535).optional().nullable(),
    webhookVerificationToken: z.string().max(65535).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.enabled) {
      const stringFields = [
        'appId',
        'appSecret',
        'wabaId',
        'phoneNumber',
        'permanentToken',
        'webhookVerificationToken',
      ] as const;

      type StringField = (typeof stringFields)[number];

      for (const field of stringFields) {
        const value = data[field as StringField];
        if (!value || value.trim().length === 0) {
          ctx.addIssue({
            path: [field],
            code: z.ZodIssueCode.too_small,
            type: 'string',
            minimum: 1,
            inclusive: true,
            message: i18n.t('general.errors.mixed.required'),
          });
        }
      }
    }
  });

type WhatsappValidationSchema = z.infer<typeof whatsAppValidationSchema>;

export function WhatsAppSettings() {
  const {data: whatsAppConfigData, isLoading: isGetWhatsAppConfigQueryLoading} =
    useGetWhatsAppConfigQuery();
  const [patchWhatsApp, {isLoading: isPatchWhatsAppConfigLoading}] =
    usePatchWhatsAppConfigMutation();

  const isLoading = isGetWhatsAppConfigQueryLoading || isPatchWhatsAppConfigLoading;

  const onSubmit: FormSubmitHandler<WhatsappValidationSchema> = async (
    values,
    _setErrors,
    reset
  ) => {
    const {enabled, ...rest} = values;
    await patchWhatsApp({
      whatsAppConfigRequestBody: {
        enabled,
        credentials: enabled
          ? {
              appId: rest?.appId ?? '',
              appSecret: rest?.appSecret ?? '',
              wabaId: rest?.wabaId ?? '',
              phoneNumber: rest?.phoneNumber ?? '',
              permanentToken: rest?.permanentToken ?? '',
              webhookVerificationToken: rest?.webhookVerificationToken ?? '',
            }
          : null,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isGetWhatsAppConfigQueryLoading}>
      {/* The form takes its values once. Remount it when the stored config changes, e.g. after the
          signup wizard on the same page connected a number, so a save can't restore old values. Keyed
          on the config itself, not the fetch time: a failed save also refetches it, and that must not
          wipe what the user typed. */}
      <Form<WhatsappValidationSchema>
        key={JSON.stringify(whatsAppConfigData)}
        mode="onSubmit"
        onSubmit={onSubmit}
        experimentalZodSchema={whatsAppValidationSchema}
        shouldWatchForUnsavedChanges
        defaultValues={{
          enabled: whatsAppConfigData?.enabled ?? false,
          appId: whatsAppConfigData?.credentials?.appId ?? '',
          appSecret: whatsAppConfigData?.credentials?.appSecret ?? '',
          wabaId: whatsAppConfigData?.credentials?.wabaId ?? '',
          phoneNumber: whatsAppConfigData?.credentials?.phoneNumber ?? '',
          permanentToken: whatsAppConfigData?.credentials?.permanentToken ?? '',
          webhookVerificationToken: whatsAppConfigData?.credentials?.webhookVerificationToken ?? '',
        }}
      >
        {(control, formApi) => {
          const isEnabled = formApi.watch('enabled');
          return (
            <VStack spacing={6}>
              <Card
                control={{
                  type: 'switch',
                  onChange: (value) => formApi.setValue('enabled', value),
                  value: isEnabled,
                }}
                title={i18n.t('page.settings.whatsApp.enableCommunication')}
                variant="inlineGrey"
                isExpanded={isEnabled}
              >
                <Grid columns={2}>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="wabaId"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.wabaId')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="phoneNumber"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.phoneNumber')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="appId"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.appId')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="appSecret"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.appSecret')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="permanentToken"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.permanentToken')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="webhookVerificationToken"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.webhookVerificationToken')}
                    />
                  </GridItem>
                </Grid>
              </Card>
              <SettingsFooter
                actions={[
                  {
                    type: 'form-button',
                    buttonType: 'submit',
                    variant: 'primary',
                    control,
                    title: i18n.t('general.actions.save'),
                    'data-testid': 'locale-config-submit',
                    isDisabled: isLoading,
                  },
                ]}
              />
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
